/**
 * Resolve template variables in a message string.
 *
 * Any context:
 *   {shop_name}                       – Merchant name
 *
 * Every per-creator context (all but collab-invitation):
 *   {creator_name}                    – Creator's TikTok username/handle
 *
 * Safety / assurance context:
 *   {TikTok_video_link(s)}            – Link(s) to the flagged TikTok video(s), one per line
 *   {risk(s)}                         – Flag type(s) across all videos (e.g. "Overconsumption, Misleading claims")
 *   {creator_risk_explanation}        – Creator-facing explanation of the risk, one per line
 *   {product_name(s)}                 – Product name(s) associated with the video
 *
 * Creator-profile / sequences context:
 *   {creator_TikTok_video_link(s)}    – Best-performing recent shoppable videos from the creator
 *   {example_videos}                  – Top-performing historical videos for the product
 *   {product_name(s)}                 – Product name(s) for the collaboration
 *   {product_description}             – Product description and why it's exciting
 *   {commission_value}                – Commission value prop, e.g. "earn $XX.XX per sale"
 *
 * Tokens without a value in the context are replaced with an empty string. Unknown
 * tokens are left intact so authoring mistakes are visible at preview time. Use
 * `findUnknownTokens` to reject them at save time and `renderTemplate` with
 * `stripUnknown` to strip them before a message reaches a creator.
 *
 * The list of tokens the resolver understands, the picker lists the frontend offers,
 * and the save/send-time validation all derive from `TEMPLATE_TOKENS` below — a single
 * source of truth. A rename here carries to every consumer via the type system.
 */

export interface TemplateContext {
  creatorName: string;
  shopName: string;
  videoUrls?: string[];
  flagTypes?: string[];
  flagExplanations?: string[];
  productNameList?: string[];
  creatorTikTokVideoLinks?: string[];
  exampleVideos?: string[];
  productDescription?: string;
  commissionValue?: string;
  creatorRiskExplanations?: string[];
}

/** UI surfaces that offer a subset of the tokens. */
export type TemplateTokenContext = "safety" | "creator-profile" | "creator-messaging" | "collab-invitation";

export type TemplateTokenBase =
  | "creator_name"
  | "shop_name"
  | "TikTok_video_link"
  | "risk"
  | "product_name"
  | "creator_TikTok_video_link"
  | "example_videos"
  | "product_description"
  | "commission_value"
  | "creator_risk_explanation";

interface TemplateTokenDefinition {
  base: TemplateTokenBase;
  pluralizable: boolean;
  contexts: readonly TemplateTokenContext[];
  resolve: (ctx: TemplateContext, fmt: (value: string) => string) => string;
}

const joinList = (xs: string[] | undefined, sep: string, fmt: (v: string) => string) =>
  xs?.length ? fmt(xs.join(sep)) : "";

const scalar = (v: string | undefined, fmt: (v: string) => string) => (v ? fmt(v) : "");

// Resolved in any order only because no token resolves to text that contains another token.
// Adding one that does would make this order-dependent.
export const TEMPLATE_TOKENS: readonly TemplateTokenDefinition[] = [
  {
    base: "creator_name",
    pluralizable: false,
    contexts: ["safety", "creator-profile", "creator-messaging"],
    resolve: (ctx, fmt) => fmt(ctx.creatorName),
  },
  {
    base: "shop_name",
    pluralizable: false,
    contexts: ["safety", "creator-profile", "creator-messaging", "collab-invitation"],
    resolve: (ctx, fmt) => fmt(ctx.shopName),
  },
  {
    base: "TikTok_video_link",
    pluralizable: true,
    contexts: ["safety"],
    resolve: (ctx, fmt) => joinList(ctx.videoUrls, "\n", fmt),
  },
  {
    base: "risk",
    pluralizable: true,
    contexts: ["safety"],
    resolve: (ctx, fmt) => joinList(ctx.flagTypes, ", ", fmt),
  },
  {
    base: "product_name",
    pluralizable: true,
    contexts: ["safety", "creator-profile"],
    resolve: (ctx, fmt) => joinList(ctx.productNameList, ", ", fmt),
  },
  {
    base: "creator_TikTok_video_link",
    pluralizable: true,
    contexts: ["creator-profile"],
    resolve: (ctx, fmt) => joinList(ctx.creatorTikTokVideoLinks, "\n", fmt),
  },
  {
    base: "example_videos",
    pluralizable: false,
    contexts: ["creator-profile"],
    resolve: (ctx, fmt) => joinList(ctx.exampleVideos, "\n", fmt),
  },
  {
    base: "product_description",
    pluralizable: false,
    contexts: ["creator-profile"],
    resolve: (ctx, fmt) => scalar(ctx.productDescription, fmt),
  },
  {
    base: "commission_value",
    pluralizable: false,
    contexts: ["creator-profile"],
    resolve: (ctx, fmt) => scalar(ctx.commissionValue, fmt),
  },
  {
    base: "creator_risk_explanation",
    pluralizable: false,
    contexts: ["safety"],
    resolve: (ctx, fmt) => joinList(ctx.creatorRiskExplanations, "\n", fmt),
  },
];

const tokenRegExp = (def: TemplateTokenDefinition): RegExp =>
  new RegExp(`\\{${def.base}${def.pluralizable ? "(?:\\(s\\))?" : ""}\\}`, "g");

const acceptedForms = (def: TemplateTokenDefinition): string[] =>
  def.pluralizable ? [`{${def.base}}`, `{${def.base}(s)}`] : [`{${def.base}}`];

const KNOWN_TOKENS: ReadonlySet<string> = new Set(TEMPLATE_TOKENS.flatMap(acceptedForms));

const displayToken = (def: TemplateTokenDefinition): string =>
  def.pluralizable ? `{${def.base}(s)}` : `{${def.base}}`;

// Must match any {token}-shaped text, not only known tokens, or unknown/legacy tokens stop surfacing.
const TOKEN_PATTERN = /\{[a-zA-Z_][a-zA-Z0-9_()]*\}/g;

export const findUnknownTokens = (template: string): string[] => {
  const unknown = new Set<string>();
  for (const match of template.matchAll(TOKEN_PATTERN)) {
    if (!KNOWN_TOKENS.has(match[0])) unknown.add(match[0]);
  }
  return [...unknown];
};

const tokensInContext = (context: TemplateTokenContext): TemplateTokenDefinition[] =>
  TEMPLATE_TOKENS.filter((def) => def.contexts.includes(context));

export const templateTokensForContext = (context: TemplateTokenContext): string[] =>
  tokensInContext(context).map(displayToken);

export const allTemplateTokens = (): string[] => TEMPLATE_TOKENS.map(displayToken);

export const findTokensOutsideContext = (template: string, context: TemplateTokenContext): string[] => {
  const allowed = new Set(tokensInContext(context).flatMap(acceptedForms));
  const outside = new Set<string>();
  for (const match of template.matchAll(TOKEN_PATTERN)) {
    if (!allowed.has(match[0])) outside.add(match[0]);
  }
  return [...outside];
};

export type RenderTemplateOptions = {
  format?: (value: string) => string;
  stripUnknown?: boolean;
  onUnknownTokens?: (unknownTokens: string[]) => void;
};

export const renderTemplate = (template: string, ctx: TemplateContext, options: RenderTemplateOptions = {}): string => {
  const { format, stripUnknown, onUnknownTokens } = options;
  let source = template;
  if (stripUnknown || onUnknownTokens) {
    const unknownTokens = findUnknownTokens(source);
    if (unknownTokens.length > 0) {
      onUnknownTokens?.(unknownTokens);
      if (stripUnknown) source = unknownTokens.reduce((acc, token) => acc.split(token).join(""), source);
    }
  }
  const fmt = format ?? ((v: string) => v);
  let resolved = source;
  for (const def of TEMPLATE_TOKENS) {
    resolved = resolved.replace(tokenRegExp(def), def.resolve(ctx, fmt));
  }
  return resolved;
};
